import React from 'react';
import { Route, Switch } from 'react-router-dom';
import Error404 from 'components/error/404';
import App from 'common/backbone-app';
import { ClientApp } from '../ClientApp';
import { FacebookBusinessExtension } from '../FacebookBusinessExtension';
import PartnerSite from '../PartnerSite/container';
import PATHS from './paths';
import { RebookReminderPerformanceRoute } from './RebookReminderPerformanceRoute';
import Wahanda from 'common/wahanda';
import { Reconnect } from 'src/federation/Reconnect';
import { trackEvent } from 'common/analytics';
import type { SharedProps } from 'reconnect-types/moduleFederation';

const AutomatedMessagingPage = () => <Reconnect name="AutomatedMessagingPage" />;

// Every step of the Reconnect campaign flow. One Route matches them all, so moving between
// steps keeps the same EmailCampaignFlowPage mounted; the page reads the step from the hash.
const CAMPAIGN_FLOW_PATHS = [
  PATHS.CAMPAIGN_PREVIEW_DEFAULT,
  PATHS.CAMPAIGN_PREVIEW_SMART_DISCOUNTS,
  PATHS.CAMPAIGN_PREVIEW_SERVICE_ON_SALE,
  PATHS.CAMPAIGN_SUMMARY_DEFAULT,
  PATHS.CAMPAIGN_SUMMARY_SMART_DISCOUNTS,
  PATHS.CAMPAIGN_SUMMARY_SERVICE_ON_SALE,
  PATHS.CAMPAIGN_SUCCESS,
];

const PROMOTIONAL_OFFER_TEMPLATE_IDS = ['smart-discounts', 'service-on-sale'];
const APP_FOR_YOUR_CLIENTS_TEMPLATE_ID = 'app-for-your-clients';

interface IRoutesProps extends React.HTMLAttributes<Element> {
  venueId: number | string;
  isPromoOffersCampaignsEnabled: boolean;
  isClientAppEnabled: boolean;
  isEmailCampaignsAccessible: boolean;
  isAppForYourClientsCampaignEnabled: boolean;
  isPartnerSiteAccessible: boolean;
  isClientFormsAccessible: boolean;
  isAppointmentMessageLogAccessible: boolean;
  tab: string;
}

export class Routes extends React.Component<IRoutesProps, {}> {
  // Reconnect re-injects its page whenever the props object changes, so each page gets one
  // object for the router's lifetime. Both come from the venue, and a venue change reloads
  // the page (UNSAFE_componentWillReceiveProps), so they can't change while mounted.
  private readonly campaignSelectionProps: SharedProps['CampaignSelectionPage'] = {
    isPromoOffersCampaignsEnabled: this.props.isPromoOffersCampaignsEnabled,
    isAppForYourClientsCampaignEnabled: this.props.isAppForYourClientsCampaignEnabled,
  };

  private readonly campaignFlowProps: SharedProps['EmailCampaignFlowPage'] = {
    minInterval: App.config.get('venue').venueMassEmailMinIntervalDays,
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.venueId !== undefined && this.props.venueId !== nextProps.venueId) {
      window.location.assign('/marketing');
    }
  }

  public setRootHash = () => {
    const { venueId, tab } = this.props;
    window.location.hash = `venue/${venueId}/${tab}`;
  };

  public emailCampaignRoutes = () => {
    const { isPromoOffersCampaignsEnabled, isAppForYourClientsCampaignEnabled } = this.props;
    // Register the campaign routes when any campaign type is on. Each route still checks its
    // own type below. (Before CCM-2115 the generic campaign was the other half of this check,
    // which left app-for-your-clients-only venues on a 404.)
    if (!isPromoOffersCampaignsEnabled && !isAppForYourClientsCampaignEnabled) {
      return <Route exact path={PATHS.CAMPAIGN_SELECTION} render={this.renderCampaignSelection} />;
    }
    return [
      <Route
        exact
        key="cmpgn-selection-route"
        path={PATHS.CAMPAIGN_SELECTION}
        render={this.renderCampaignSelection}
      />,
      <Route exact key="cmpgn-flow" path={CAMPAIGN_FLOW_PATHS} render={this.renderCampaignFlow} />,
    ];
  };

  public clientAppRoutes = () => {
    const { isClientAppEnabled } = this.props;
    if (!isClientAppEnabled) {
      return null;
    }
    return <Route exact path={PATHS.CLIENT_APP_LANDING} component={ClientApp} />;
  };

  public partnerSiteRoutes = () => {
    const { isPartnerSiteAccessible } = this.props;
    if (!isPartnerSiteAccessible) {
      return null;
    }
    return <Route exact path={PATHS.PARTNER_SITE_SETTINGS} component={PartnerSite} />;
  };

  public fbeRoutes = () => {
    return <Route exact path={PATHS.FBE} component={FacebookBusinessExtension} />;
  };

  public automatedMessagingRoutes = () => {
    return <Route exact path={PATHS.AUTOMATED_MESSAGING} component={AutomatedMessagingPage} />;
  };

  public clientFormsRoutes = () => {
    const { isClientFormsAccessible, venueId, tab } = this.props;
    if (!isClientFormsAccessible) {
      return null;
    }
    return (
      <Route
        exact
        path={[PATHS.CLIENT_FORMS, PATHS.CLIENT_FORMS_CREATE]}
        component={() => {
          return (
            <Reconnect
              name="ConsultationFormsPage"
              props={{
                notifyFn: Wahanda.ToastNotifications.sendMessage,
                legacyLang: Wahanda.lang,
                backboneEx: {
                  trackEvent,
                },
                isCreateClientFormVisible: window.location.hash.endsWith('/create'),
                navigateToCreateClientForm: () =>
                  (window.location.hash = `venue/${venueId}/${tab}/create`),
                navigateToClientForms: () => (window.location.hash = `venue/${venueId}/${tab}`),
              }}
            />
          );
        }}
      />
    );
  };

  public messageLogRoutes = () => {
    return (
      <Route
        exact
        path={PATHS.MESSAGE_LOG}
        component={() => {
          return <Reconnect name="MessageLogPage" props={{}} />;
        }}
      />
    );
  };

  public appointmentMessageLogRoutes = () => {
    return (
      <Route
        exact
        path={PATHS.APPOINTMENT_MESSAGE_LOG}
        component={() => {
          return <Reconnect name="AppointmentMessageLogPage" props={{}} />;
        }}
      />
    );
  };

  public rebookReminderPerformanceRoutes = () => {
    return (
      <Route
        exact
        path={PATHS.REBOOK_REMINDER_PERFORMANCE}
        render={() => <RebookReminderPerformanceRoute venueId={this.props.venueId} />}
      />
    );
  };

  public isCampaignTypeEnabled = (templateId: string) => {
    const { isPromoOffersCampaignsEnabled, isAppForYourClientsCampaignEnabled } = this.props;
    if (PROMOTIONAL_OFFER_TEMPLATE_IDS.includes(templateId)) {
      return isPromoOffersCampaignsEnabled;
    }
    if (templateId === APP_FOR_YOUR_CLIENTS_TEMPLATE_ID) {
      return isAppForYourClientsCampaignEnabled;
    }
    // Unknown ids reach the flow page, which sends them back to the campaign list.
    return true;
  };

  public renderCampaignSelection = () => (
    <Reconnect name="CampaignSelectionPage" props={this.campaignSelectionProps} />
  );

  public renderCampaignFlow = ({ match }) => {
    // A venue without this campaign type goes back to the landing page, as before.
    if (!this.isCampaignTypeEnabled(match.params.templateId)) {
      this.setRootHash();
      return this.renderCampaignSelection();
    }
    return <Reconnect name="EmailCampaignFlowPage" props={this.campaignFlowProps} />;
  };

  public render() {
    const { isEmailCampaignsAccessible, isAppointmentMessageLogAccessible } = this.props;
    if (window.location.hash === '') {
      this.setRootHash();
    }
    return (
      <Switch>
        {isEmailCampaignsAccessible && this.emailCampaignRoutes()}
        {this.clientAppRoutes()}
        {this.partnerSiteRoutes()}
        {this.fbeRoutes()}
        {this.automatedMessagingRoutes()}
        {this.clientFormsRoutes()}
        {this.messageLogRoutes()}
        {isAppointmentMessageLogAccessible && this.appointmentMessageLogRoutes()}
        {this.rebookReminderPerformanceRoutes()}
        <Route component={Error404} />
      </Switch>
    );
  }
}
